import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { IconX, IconUser } from 'components/Icons';
import { useUpdateUser } from '../../../hooks/useUpdateUser';
import Loading from '../../../components/Loading';
import { useHoneGetUserInfo } from 'hooks/useHoneGetUserInfo';
import { useParams } from 'react-router-dom';
import { ProfileEntryParams } from '../NewTable/types';
import { useGetAiPrompt } from 'hooks/ai/useGetAiPrompt';
import './NameModal.scss';
import classNames from 'classnames';

interface NameModalProps {
  isOpen: boolean;
  onSuccess?: () => void;
  onClose: () => void;
  userId: string;
  uid?: string;
  locationId?: string;
}

interface NameFormData {
  userId: string;
  name: string;
  description: string;
}

export const NameModal: React.FC<NameModalProps> = ({ isOpen, onClose, onSuccess, userId, locationId }) => {
  const { locationId: routeLocationId } = useParams<ProfileEntryParams>();
  const { data, isLoading, refetch } = useHoneGetUserInfo(userId, locationId || routeLocationId, isOpen);
  const userData = data as HoneLocationUser;
  const [showAiSuggestions, setShowAiSuggestions] = useState(false);
  const [showNameAiSuggestions, setShowNameAiSuggestions] = useState(false);
  const { data: aiPrompts, isLoading: aiLoading } = useGetAiPrompt('User Description');
  const { data: nameAiPrompts, isLoading: nameAiLoading } = useGetAiPrompt('User Name');

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<NameFormData>({
    defaultValues: {
      userId,
      name: userData?.name || '',
      description: userData?.description || '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      // Force refetch fresh data when modal opens
      refetch();
    }
  }, [isOpen, refetch]);

  // Watch the description field for changes
  const descriptionValue = watch('description');

  // Filter AI prompts based on description input
  const filteredAiPrompts = useMemo(() => {
    if (!aiPrompts || !descriptionValue || descriptionValue.length < 2) {
      return aiPrompts || [];
    }

    const filterText = descriptionValue.toLowerCase().trim();
    return aiPrompts.filter(
      prompt => prompt.name.toLowerCase().includes(filterText) || prompt.prompt.toLowerCase().includes(filterText)
    );
  }, [aiPrompts, descriptionValue]);

  // Show suggestions automatically when typing and there are filtered results
  const shouldShowFilteredSuggestions =
    descriptionValue && descriptionValue.length >= 2 && filteredAiPrompts.length > 0;
  const mutation = useUpdateUser({
    onSuccess: () => {
      // Reset the form to initial values
      reset({ userId, name: userData?.name || '', description: userData?.description || '' });
      // Close the modal
      onSuccess?.();
      onClose();
    },
  });

  // Reset form when modal is opened with new values
  useEffect(() => {
    if (isOpen) {
      // Force refetch fresh data when modal opens
      reset({ userId, name: userData?.name || '', description: userData?.description || '' });
      // Reset AI suggestions state
      setShowAiSuggestions(false);
      setShowNameAiSuggestions(false);
    }
  }, [isOpen, userId, userData, reset, refetch]);

  const onSubmit = (data: NameFormData) => {
    mutation.mutate({ ...data, locationId, uid: userId });
  };

  const handleAiSuggestionSelect = (suggestion: string) => {
    setValue('description', suggestion);
    setShowAiSuggestions(false);
  };

  const handleNameAiSuggestionSelect = (suggestion: string) => {
    setValue('name', suggestion);
    setShowNameAiSuggestions(false);
  };

  if (!isOpen) return null;

  if (isLoading) return <Loading loadingCircleClass="Loading-circle-small" />;
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div
        className={classNames('modal-content user-profile name-modal', {
          'suggestions-visible': showAiSuggestions || shouldShowFilteredSuggestions,
        })}
        data-testid="modal-content"
      >
        <div className="modal-header">
          <h2>Update User</h2>
          <button onClick={onClose} className="close-button">
            <IconX />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <input
              type="text"
              id="name"
              {...register('name', {
                required: 'Name is required',
                minLength: {
                  value: 2,
                  message: 'Name must be at least 2 characters',
                },
              })}
            />
            {errors.name && <p className="error">{errors.name.message}</p>}
            {showNameAiSuggestions && nameAiPrompts && nameAiPrompts.length > 0 && (
              <div className="ai-suggestions-container">
                <p className="ai-suggestions-title">Select a name:</p>
                <div className="ai-suggestions-list">
                  {nameAiPrompts.map(prompt => (
                    <button
                      key={prompt.id}
                      type="button"
                      className="ai-suggestion-btn"
                      onClick={() => handleNameAiSuggestionSelect(prompt.prompt)}
                      title={prompt.prompt}
                    >
                      {prompt.prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="form-group info">
            <div className="form-group-header">
              <label htmlFor="description">Job description</label>
              <button
                type="button"
                onClick={() => setShowAiSuggestions(!showAiSuggestions)}
                className="ai-suggestions-btn"
                disabled={aiLoading}
              >
                {aiLoading ? 'Loading...' : '✨ AI Suggestions'}
              </button>
            </div>
            <textarea
              className="form-group__textarea"
              placeholder="Add a description (optional)"
              {...register('description')}
              rows={3}
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel">
              Cancel
            </button>
            <button type="submit" className="submit" disabled={mutation.isPending}>
              {mutation.isPending ? <Loading loadingCircleClass="Loading-circle-small" /> : 'Update User'}
            </button>
          </div>
          <div
            className={`ai-suggestions-container ${
              showAiSuggestions || shouldShowFilteredSuggestions ? 'visible' : 'hidden'
            }`}
          >
            {(showAiSuggestions || shouldShowFilteredSuggestions) && (
              <>
                <p className="ai-suggestions-title">
                  {shouldShowFilteredSuggestions
                    ? `Filtered job descriptions (${filteredAiPrompts.length})`
                    : 'Select a job description:'}
                </p>
                <div className="ai-suggestions-list">
                  {(shouldShowFilteredSuggestions ? filteredAiPrompts : aiPrompts || []).map(prompt => (
                    <div key={prompt.id} className="ai-suggestion-item">
                      <div className="ai-suggestion-header">
                        ✨<span className="ai-suggestion-name">{prompt.name}</span>
                      </div>
                      <button
                        type="button"
                        className="ai-suggestion-btn"
                        onClick={() => handleAiSuggestionSelect(prompt.prompt)}
                        title={prompt.prompt}
                      >
                        {prompt.prompt}
                      </button>
                    </div>
                  ))}
                </div>
                {shouldShowFilteredSuggestions && filteredAiPrompts.length === 0 && (
                  <p className="no-suggestions">No matching suggestions found</p>
                )}
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
